<template>
  <div class="dock-live-panel">
    <!-- FlightHub-style status strip -->
    <div class="dlp-header">
      <div class="dlp-status" :class="online ? 'ok' : 'bad'">
        <span class="dot"></span>
        <span>{{ modeText }}</span>
      </div>
      <div class="dlp-metrics">
        <a-tooltip title="Environment temperature">
          <span class="metric">🌡 {{ envTemp }}</span>
        </a-tooltip>
        <a-tooltip title="Network rate">
          <span class="metric">⇅ {{ netRate }}</span>
        </a-tooltip>
        <a-tooltip title="Network signal quality">
          <span class="metric"><SignalFilled /> {{ netQuality }}</span>
        </a-tooltip>
        <a-tooltip title="Drone in dock">
          <span class="metric"><RocketOutlined :style="{ color: deviceInfo.dock.basic_osd?.drone_in_dock ? '#35f28c' : '#888' }"/></span>
        </a-tooltip>
      </div>
    </div>

    <!-- Live / Actions tabs -->
    <div class="dlp-tabs">
      <div class="dlp-tab" :class="{ active: tab === 'live' }" @click="tab = 'live'">Live</div>
      <div class="dlp-tab" :class="{ active: tab === 'actions' }" @click="tab = 'actions'">Actions</div>
    </div>

    <!-- LIVE: dock camera -->
    <div v-show="tab === 'live'" class="dlp-video-wrap">
      <video ref="videoRef" autoplay muted playsinline class="dlp-video"></video>
      <div v-if="!playing" class="dlp-video-overlay">
        <template v-if="online">
          <span class="fz12">{{ liveMsg }}</span>
          <a-button size="small" type="primary" class="mt10" :loading="connecting" @click="tryPlay">
            {{ connecting ? 'Connecting…' : 'Start dock camera' }}
          </a-button>
        </template>
        <template v-else>
          <span class="fz12" style="color:#999">Dock camera — available when the dock is online</span>
        </template>
      </div>
      <span v-if="playing" class="dlp-live-badge">● LIVE</span>
    </div>

    <!-- ACTIONS: existing device control embedded -->
    <div v-show="tab === 'actions'" class="dlp-actions">
      <DockControlPanel :sn="props.sn" :deviceInfo="props.deviceInfo" class="dlp-embedded" @close-control-panel="tab = 'live'" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, onUnmounted, ref } from 'vue'
import { SignalFilled, RocketOutlined } from '@ant-design/icons-vue'
import flvjs from 'flv.js'
import DockControlPanel from './DockControlPanel.vue'
import { DeviceInfoType, EDockModeCode } from '/@/types/device'

const props = defineProps<{
  sn: string,
  deviceInfo: DeviceInfoType,
}>()

const tab = ref<'live' | 'actions'>('live')
const videoRef = ref<HTMLVideoElement>()
const playing = ref(false)
const connecting = ref(false)
const liveMsg = ref('Dock surveillance camera')
let flvPlayer: flvjs.Player | null = null

const online = computed(() => {
  const mc = props.deviceInfo.dock.basic_osd?.mode_code
  return mc !== undefined && mc !== EDockModeCode.Disconnected
})
const modeText = computed(() => EDockModeCode[props.deviceInfo.dock.basic_osd?.mode_code] ?? 'Disconnected')
const envTemp = computed(() => {
  const t = props.deviceInfo.dock.basic_osd?.environment_temperature
  return (t !== undefined && t !== null) ? `${t} °C` : '--'
})
const netRate = computed(() => {
  const r = props.deviceInfo.dock.basic_osd?.network_state?.rate
  return (r !== undefined && r !== null) ? `${r} KB/s` : '--'
})
const netQuality = computed(() => {
  const q = props.deviceInfo.dock.basic_osd?.network_state?.quality
  return (q !== undefined && q !== null) ? q : '--'
})

function authHeaders (): Record<string, string> {
  return { 'x-auth-token': localStorage.getItem('x-auth-token') || '', 'Content-Type': 'application/json' }
}

/* Nađi video_id dok-kamere iz live-capacity (sn/camera_index/video_index) */
async function dockVideoId (): Promise<string | null> {
  const wsId = localStorage.getItem('workspace_id') || ''
  const base = (window as any).CURRENT_CONFIG?.baseURL || '/'
  const res = await fetch(`${base}manage/api/v1/live/capacity`, { headers: authHeaders() }).then(r => r.json()).catch(() => null)
  const list = res?.data || []
  const dev = list.find((d: any) => d.sn === props.sn) || list[0]
  const cam = dev?.camera_list?.[0]
  const vid = cam?.video_list?.[0]
  if (dev && cam && vid) return `${dev.sn}/${cam.camera_index}/${vid.video_index}`
  return null
}

/* Pusti player-friendly _web stream (relej ga drži živim + ima audio) */
async function tryPlay () {
  stopPlay()
  connecting.value = true
  liveMsg.value = 'Učitavam sliku…'
  try {
    // ime streama iz capacity → {sn}-{camera_index}_web
    const videoId = await dockVideoId() || `${props.sn}/165-0-7/normal-0`
    const parts = videoId.split('/')
    const stream = `${parts[0]}-${parts[1]}_web`

    if (flvjs.isSupported()) {
      flvPlayer = flvjs.createPlayer({
        type: 'flv', isLive: true, url: `${location.origin}/live-flv/${stream}.flv`,
      }, { enableStashBuffer: false, stashInitialSize: 128, liveBufferLatencyChasing: true })
      flvPlayer.attachMediaElement(videoRef.value as HTMLVideoElement)
      flvPlayer.on(flvjs.Events.ERROR, () => { stopPlay(); liveMsg.value = 'Stream nije krenuo — probaj ponovo za par sekundi' })
      flvPlayer.load()
      await (videoRef.value as HTMLVideoElement).play().catch(() => { /* autoplay */ })
      playing.value = true
    } else {
      const video = videoRef.value as HTMLVideoElement
      video.src = `${location.origin}/live-hls/${stream}.m3u8`
      await video.play().catch(() => { /* autoplay */ })
      playing.value = true
    }
  } catch (e: any) {
    stopPlay()
    liveMsg.value = 'Stream nije krenuo — probaj ponovo za par sekundi'
  } finally {
    connecting.value = false
  }
}

function stopPlay () {
  if (flvPlayer) {
    try { flvPlayer.destroy() } catch { /* already destroyed */ }
    flvPlayer = null
  }
  playing.value = false
}

onUnmounted(stopPlay)
</script>

<style lang="scss" scoped>
.dock-live-panel {
  position: absolute;
  left: calc(100% + 10px);
  top: 0;
  width: 380px;
  background: #000;
  color: #fff;
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.6);

  .dlp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px;
    background: #1e1e1e;
    border-bottom: 1px solid #515151;

    .dlp-status {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 600;
      .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
      &.ok { color: #35f28c; .dot { background: #35f28c; } }
      &.bad { color: #ff4d4f; .dot { background: #ff4d4f; } }
    }
    .dlp-metrics {
      display: flex;
      gap: 12px;
      .metric { font-size: 12px; color: #ddd; }
    }
  }

  .dlp-tabs {
    display: flex;
    border-bottom: 1px solid #515151;
    .dlp-tab {
      flex: 1;
      text-align: center;
      padding: 6px 0;
      cursor: pointer;
      color: #999;
      &.active { color: #fff; border-bottom: 2px solid #2d8cf0; background: #141414; }
      &:hover { color: #fff; }
    }
  }

  .dlp-video-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #111;

    .dlp-video { width: 100%; height: 100%; object-fit: contain; }
    .dlp-video-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 0 16px;
    }
    .dlp-live-badge {
      position: absolute;
      top: 6px;
      left: 8px;
      color: #ff4d4f;
      font-size: 12px;
      font-weight: 700;
      text-shadow: 0 0 4px #000;
    }
  }

  .dlp-actions {
    max-height: 420px;
    overflow-y: auto;

    :deep(.dock-control-panel) {
      position: static !important;
      left: auto !important;
      top: auto !important;
      width: 100% !important;
      box-shadow: none;
    }
    :deep(.dock-control-panel-header) {
      display: none;
    }
  }
}
</style>
